/**
 * Phép tính thuần của màn Biên bản cuộc họp: định dạng thời lượng, gom lượt nói, chọn đoạn theo mốc phát, kiểm tệp,
 * phân loại lỗi micro. Không đụng DOM hay React để test được bằng `node --test`.
 */
import type { Doan, NguoiNoi, Phien, TomTatGhi, TrangThaiPhien, ViecCanLam } from '../types.ts';

const hai = (n: number) => String(n).padStart(2, '0');

/** Giây → `hh:mm:ss`, luôn đủ ba phần để các cột và mốc thẳng hàng. Không có số liệu thì trả `null`. */
export function dinhDangThoiLuong(giay: number | null | undefined): string | null {
  if (giay === null || giay === undefined || !Number.isFinite(giay) || giay < 0) return null;
  const tong = Math.floor(giay);
  return `${hai(Math.floor(tong / 3600))}:${hai(Math.floor((tong % 3600) / 60))}:${hai(tong % 60)}`;
}

export interface LuotNoi {
  khoa: string;
  speaker: string | null;
  start: number;
  end: number;
  doan: Doan[];
}

/** Gom các đoạn liền nhau của cùng một người thành một lượt nói; đoạn đầu vào có thể chưa sắp theo `index`. */
export function gomLuotNoi(ds: readonly Doan[]): LuotNoi[] {
  const ra: LuotNoi[] = [];
  for (const d of [...ds].sort((a, b) => a.index - b.index)) {
    const cuoi = ra[ra.length - 1];
    if (cuoi && cuoi.speaker === d.speaker) {
      cuoi.doan.push(d);
      cuoi.end = Math.max(cuoi.end, d.end);
    } else {
      ra.push({ khoa: d.id, speaker: d.speaker, start: d.start, end: d.end, doan: [d] });
    }
  }
  return ra;
}

/**
 * Đoạn đang phát ở mốc `giay`: đoạn cuối cùng đã bắt đầu. Trong khoảng lặng giữa hai đoạn vẫn giữ đoạn trước,
 * để phần nổi không nhấp nháy; chưa tới đoạn đầu thì không có đoạn nào. `ds` phải sắp theo thời gian.
 */
export function timDoanDangPhat(ds: readonly Pick<Doan, 'id' | 'start'>[], giay: number): string | null {
  let dau = 0;
  let cuoi = ds.length - 1;
  let ket: string | null = null;
  while (dau <= cuoi) {
    const giua = (dau + cuoi) >> 1;
    const d = ds[giua]!;
    if (d.start <= giay) {
      ket = d.id;
      dau = giua + 1;
    } else {
      cuoi = giua - 1;
    }
  }
  return ket;
}

/** `SPEAKER_03` → 3, để màu và số thứ tự không đổi khi đoạn đầu tiên đổi người nói. Nhãn lạ thì băm. */
export function chiSoNguoiNoi(label: string): number {
  const so = /(\d+)\s*$/.exec(label);
  if (so) return Number(so[1]);
  let bam = 0;
  for (const c of label) bam = (bam * 31 + c.charCodeAt(0)) % 9973;
  return bam;
}

/** Vị trí tông màu ổn định theo nhãn, không theo thứ tự xuất hiện. */
export function viTriMauNguoiNoi(label: string, soMau: number): number {
  return chiSoNguoiNoi(label) % soMau;
}

/** Tên người dùng đặt cho nhãn, bỏ khoảng trắng; `null` khi chưa đặt. */
export function tenDaDat(label: string, ds: readonly NguoiNoi[]): string | null {
  return ds.find((n) => n.label === label)?.name?.trim() || null;
}

/** Danh sách người chọn được khi đổi người nói: đủ người của phiên, cộng nhãn chỉ đoạn còn mang. */
export function nhanNguoiNoi(speakers: readonly NguoiNoi[], ds: readonly Doan[]): string[] {
  const nhan = new Set(speakers.map((s) => s.label));
  for (const d of ds) if (d.speaker) nhan.add(d.speaker);
  return [...nhan].sort((a, b) => chiSoNguoiNoi(a) - chiSoNguoiNoi(b));
}

/** Phiên còn đang chạy nền, màn phải tự hỏi lại. */
export function dangXuLy(status: TrangThaiPhien): boolean {
  return status === 'QUEUED' || status === 'TRANSCRIBING' || status === 'SUMMARIZING';
}

/** Tự làm mới khi còn việc chạy nền, kể cả lượt tóm tắt lại của phiên đã xong. */
export function canTuLamMoi(p: Pick<Phien, 'status' | 'summary_status'> | undefined): boolean {
  if (!p) return false;
  return dangXuLy(p.status) || p.summary_status === 'PENDING';
}

/** Đã có thể đọc bản chép lời chưa: trước khi chép xong thì không có gì để đọc. */
export function coBanChepLoi(status: TrangThaiPhien): boolean {
  return status === 'SUMMARIZING' || status === 'DONE' || status === 'FAILED';
}

export const CAC_BUOC = ['QUEUED', 'TRANSCRIBING', 'SUMMARIZING', 'DONE'] as const;
export type BuocXuLy = (typeof CAC_BUOC)[number];

/** Vị trí bước hiện tại trong `CAC_BUOC`; `null` với phiên lỗi vì không biết chắc đã dừng ở bước nào. */
export function viTriBuoc(status: TrangThaiPhien): number | null {
  return status === 'FAILED' ? null : CAC_BUOC.indexOf(status);
}

/** Trần tải lên mặc định (`BIEN_BAN_UPLOAD_MAX_MB`). Kiểm sớm cho đỡ chờ; máy chủ vẫn là cổng thật. */
export const TOI_DA_MB = 500;
const TOI_DA_BYTE = TOI_DA_MB * 1024 * 1024;

/** Khớp `DUOI_NHAN` của `modules/bien-ban/backend/src/phien/loai-tep.ts`: máy chủ xét đuôi rồi xét chữ ký đầu tệp. */
export const DUOI_NHAN_DUOC = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'flac', 'webm', 'mp4', 'mov'] as const;

export type LoiTep = 'sai-dinh-dang' | 'qua-lon' | 'rong';

/** Xét đuôi tệp chứ không xét `type`: có trình duyệt không báo `type`, và máy chủ cũng quyết theo đuôi. */
export function kiemTepGhiAm(tep: { name: string; size: number }): LoiTep | null {
  const duoi = tep.name.includes('.') ? tep.name.split('.').pop()!.toLowerCase() : '';
  if (!(DUOI_NHAN_DUOC as readonly string[]).includes(duoi)) return 'sai-dinh-dang';
  if (tep.size === 0) return 'rong';
  if (tep.size > TOI_DA_BYTE) return 'qua-lon';
  return null;
}

/** Tiêu đề mặc định: tên tệp bỏ đuôi. Tên chỉ có đuôi (`.mp3`) thì giữ nguyên, kẻo tiêu đề rỗng. */
export function tieuDeTuTenTep(ten: string): string {
  const cham = ten.lastIndexOf('.');
  return (cham > 0 ? ten.slice(0, cham) : ten).trim();
}

/** Tách ô nhiều dòng thành danh sách ý, bỏ dòng trống. */
export function tachDong(chu: string): string[] {
  return chu.split('\n').map((d) => d.trim()).filter(Boolean);
}

export interface FormTomTat {
  overview: string;
  key_points: string;
  decisions: string;
  action_items: Array<{ task: string; owner: string; due: string }>;
}

export function formTuTomTat(t: TomTatGhi | null): FormTomTat {
  return {
    overview: t?.overview ?? '',
    key_points: (t?.key_points ?? []).join('\n'),
    decisions: (t?.decisions ?? []).join('\n'),
    action_items: (t?.action_items ?? []).map((v) => ({ task: v.task, owner: v.owner ?? '', due: v.due ?? '' })),
  };
}

const rongThanhNull = (s: string) => s.trim() || null;

/** Dựng thân `PUT /summary`: bỏ việc không có nội dung, ô trống thành `null`. */
export function thanTomTat(f: FormTomTat): TomTatGhi {
  const viec: ViecCanLam[] = f.action_items
    .filter((v) => v.task.trim())
    .map((v) => ({ task: v.task.trim(), owner: rongThanhNull(v.owner), due: rongThanhNull(v.due) }));
  return { overview: f.overview.trim(), key_points: tachDong(f.key_points), decisions: tachDong(f.decisions), action_items: viec };
}

/** Một việc có người phụ trách hay hạn mà thiếu nội dung: lưu là mất, nên báo trước. */
export function viecThieuNoiDung(f: FormTomTat): number[] {
  return f.action_items.flatMap((v, i) => (!v.task.trim() && (v.owner.trim() || v.due.trim()) ? [i] : []));
}

/** Sau lần cuộn tay gần nhất, màn nhường người dùng trong khoảng này rồi mới tự cuộn lại. */
export const NHUONG_CUON_TAY_MS = 8_000;

export function nenTuCuon(bayGio: number, lanCuonTay: number | null, nhuong = NHUONG_CUON_TAY_MS): boolean {
  return lanCuonTay === null || bayGio - lanCuonTay >= nhuong;
}

export type LyDoMicro = 'KHONG_HO_TRO' | 'KHONG_BAO_MAT' | 'TU_CHOI' | 'KHONG_CO_THIET_BI' | 'DANG_BAN' | 'KHONG_CO_TIENG' | 'KHAC';

/**
 * Lý do micro không mở, mỗi lý do một cách xử lý. Qua HTTP thường trình duyệt không hề hỏi quyền, nên phải tách khỏi
 * "bị từ chối"; phân loại theo `DOMException.name` vì `message` đổi theo trình duyệt.
 */
export function phanLoaiLoiMicro(loi: unknown, baoMat: boolean): LyDoMicro {
  if (!baoMat) return 'KHONG_BAO_MAT';
  switch ((loi as { name?: unknown } | null)?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'TU_CHOI';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'KHONG_CO_THIET_BI';
    case 'NotReadableError':
    case 'AbortError':
      return 'DANG_BAN';
    default:
      return 'KHAC';
  }
}

/** Trình duyệt đủ khả năng ghi âm không; thiếu `mediaDevices` trong ngữ cảnh không bảo mật là lý do khác. */
export function lyDoKhongGhiDuoc(moiTruong: { baoMat: boolean; coMediaDevices: boolean; coMediaRecorder: boolean }): LyDoMicro | null {
  if (!moiTruong.baoMat) return 'KHONG_BAO_MAT';
  if (!moiTruong.coMediaDevices || !moiTruong.coMediaRecorder) return 'KHONG_HO_TRO';
  return null;
}

const KIEU_GHI_AM = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'] as const;

/** Kiểu ghi đầu tiên trình duyệt nhận; `undefined` để trình duyệt tự chọn (Safari cũ chỉ có `audio/mp4`). */
export function chonKieuGhiAm(hoTro: (kieu: string) => boolean): string | undefined {
  return KIEU_GHI_AM.find(hoTro);
}

/** Tên tệp ghi âm, không dấu và không ký tự lạ để đi qua multipart an toàn. */
export function tenTepGhiAm(kieu: string, luc: Date): string {
  const duoi = kieu.includes('mp4') ? 'm4a' : kieu.includes('ogg') ? 'ogg' : 'webm';
  const ngay = `${luc.getFullYear()}${hai(luc.getMonth() + 1)}${hai(luc.getDate())}`;
  return `ghi-am-${ngay}-${hai(luc.getHours())}${hai(luc.getMinutes())}${hai(luc.getSeconds())}.${duoi}`;
}
